
/**
 * Prompt recipes — curated, ready-to-run prompts surfaced by the
 * {@link PromptRecipesPopup} when a user types "/" in the writing-plan chat.
 *
 * These are pure prompt presets: selecting one composes its `prompt` text into
 * the chat input (it does not auto-send), so the user can tweak before sending.
 * The catalogue below is the static English source of truth (and the order in
 * which recipes appear). Display strings — title, description and the prompt
 * itself — are localised: components read them via {@link usePromptRecipes},
 * which resolves each field through i18n (key `promptRecipes.recipes.<id>.*`)
 * and falls back to the English literal here when a translation is missing.
 * So flipping the UI language flips the recipes, prompt included. To add or
 * edit a recipe: change it here, then mirror the strings into the non-English
 * locale files (`src/i18n/locales/{es,de,fr}/translation.json`).
 *
 * Content mirrors the "Chat Prompt Picker v3" design (Prompt recipes popup).
 */

export type RecipeCategory = "insights" | "storyboard" | "plan" | "review" | "write";

export interface PromptRecipe {
  /** Stable id (used as React key and for tests). */
  id: string;
  category: RecipeCategory;
  /** Short title shown in the list row. */
  title: string;
  /** One-line supporting description. */
  description: string;
  /** Full prompt text inserted into the chat input on select. */
  prompt: string;
}

/** Display labels for category headings and tabs, in display order. */
export const RECIPE_CATEGORIES: { key: RecipeCategory; label: string }[] = [
  { key: "insights", label: "Insights" },
  { key: "storyboard", label: "Storyboard" },
  { key: "plan", label: "Plan" },
  { key: "review", label: "Review" },
  { key: "write", label: "Write" }
];

export const PROMPT_RECIPES: PromptRecipe[] = [
  // ─── Insights ───────────────────────────────────────────────────────────
  {
    id: "summarise-tender-spec",
    category: "insights",
    title: "Summarise tender specification",
    description: "Explains the key requirements, criteria, scope and deadlines",
    prompt:
      "Provide a comprehensive yet concise summary of the tender specification, highlighting the key requirements, evaluation criteria, scope of work, critical deadlines, and any unique or particularly important aspects. Organise the summary in a way that makes it easy to understand the client's priorities and what will be most important in the evaluation process."
  },
  {
    id: "requirements-matching",
    category: "insights",
    title: "Requirements matching",
    description: "Match every tender requirement against our content library",
    prompt:
      "Extract all requirements from the tender documents, then search our content library to check whether each one can be matched with existing content. For every requirement, indicate whether it is fully matched, partially matched, or has no supporting content, citing the relevant library material where it exists. Highlight any gaps where we have no content to address a requirement so they can be prioritised."
  },
  {
    id: "customer-research",
    category: "insights",
    title: "Customer research",
    description: "Background on the buyer and why they're tendering",
    prompt:
      "Research this customer using both the tender documents and the internet. Find background on the buyer — who they are, their context, recent news, strategic priorities, and any drivers, pressures, or problems that would have led them to release this tender. Pull relevant detail from the tender documents and supplement it with publicly available information, then summarise what it tells us about their motivations and what they want from this procurement."
  },
  {
    id: "key-dates",
    category: "insights",
    title: "Key dates",
    description: "All key dates and deadlines in the tender",
    prompt:
      "Find and extract all key dates from the tender documents. Include the submission deadline, clarification or question deadlines, any site visits or supplier engagement events, the tender return date, contract start and end dates, and any other milestones. List each date clearly alongside what it relates to, in chronological order, and flag anything that is implied but not given an explicit date."
  },

  // ─── Storyboard ─────────────────────────────────────────────────────────
  {
    id: "explain-storyboarding",
    category: "storyboard",
    title: "Explain storyboarding",
    description: "What storyboarding is and how it works here",
    prompt:
      "Explain what storyboarding is and how it works within this platform. Cover what a storyboard is in the context of a bid, why it's useful, the stages involved, and how to use the storyboarding features here to plan and structure a winning response. Keep it clear and practical, as a guide for someone new to the feature."
  },
  {
    id: "build-narrative-arc",
    category: "storyboard",
    title: "Build a narrative arc",
    description: "Challenge, solution, outcomes story beats",
    prompt:
      "Build a narrative arc for our bid: buyer's challenge then our understanding then our solution then proven outcomes. Draft the key beats that should run through our entire response."
  },
  {
    id: "analyse-win-themes",
    category: "storyboard",
    title: "Analyse win themes",
    description: "Assess and research stronger, better-targeted win themes",
    prompt:
      "Analyse our current win themes for this bid and assess how strong and well-targeted they are against what the buyer values most. Then research more widely — using the tender documents, the internet, and what is known about the bidding organisation and its strengths — to find sharper, more compelling win themes. Suggest improved or additional win themes, explain why each would resonate with this specific buyer, and back them with supporting evidence."
  },
  {
    id: "add-client-notes",
    category: "storyboard",
    title: "Add client notes",
    description: "Slot customer meeting & site notes into the storyboard",
    prompt:
      "If the user has attached meeting notes, take the meeting and site visit notes captured from the customer and add them into the relevant storyboarding sections:\n\n* General - important context for the whole bid\n* Pain points - any customer stated pain points related to the tender released\n* Solution - the solution proposed to the customer\n* Differentiators - how you differ from other competitors\n* Win themes - key themes to go through the bid to make your offering stand out\n\nIf there are no meeting notes attached or visible in context, ask the user if they can attach the meeting notes or client notes to the chat so you can add them."
  },

  // ─── Plan ───────────────────────────────────────────────────────────────
  // Mirrors the quick-action chips above the writing-plan input on the proposal
  // preview page (see WritingPlan.tsx PRESET_CHIPS), plus a "Shorten plan" preset.
  {
    id: "suggest-new-content",
    category: "plan",
    title: "Suggest new content",
    description: "Relevant library content to substantiate the answer",
    prompt:
      "Suggest new content from the library that is directly relevant to the question and will effectively substantiate the answer that I could use in my response."
  },
  {
    id: "analyse-evaluation-criteria",
    category: "plan",
    title: "Analyse evaluation criteria",
    description: "What the buyer is looking for to score highly",
    prompt:
      "Find the evaluation criteria relevant to this question and analyse what the buyer is looking for in order to produce a high-scoring response."
  },
  {
    id: "add-evidence-to-plan",
    category: "plan",
    title: "Add more evidence",
    description: "Pull supporting evidence into the writing plan",
    prompt:
      "Find relevant evidence from the library that would substantiate this response effectively, and then add it concisely and at a high level to the relevant parts of the writing plan."
  },
  {
    id: "shorten-plan",
    category: "plan",
    title: "Shorten plan",
    description: "Tighten the writing plan without losing key points",
    prompt:
      "Make the current writing plan shorter and more concise without losing any of the key points, evidence, or coverage of the evaluation criteria. Tighten the language, remove repetition and anything non-essential, and keep the plan focused on what will score."
  },

  // ─── Review ─────────────────────────────────────────────────────────────
  {
    id: "score-response",
    category: "review",
    title: "Score this response against criteria",
    description: "Where it performs well and 3 ways to improve",
    prompt:
      "Score this response against the tender's own evaluation criteria as they apply to this question, one row per criterion, or the default dimensions if it defines none. Check the outline first: do not suggest adding anything that has its own section or is covered elsewhere in the bid; at most suggest a cross-reference. Show the marks as the scored breakdown, not as written analysis. Keep your chat text to the headline judgement plus the two or three improvements most likely to raise the score, and offer to make them."
  },
  {
    id: "hit-target-word-count",
    category: "review",
    title: "Hit the target word count",
    description: "Tighten or expand to land exactly on target",
    prompt:
      "Look at the target word count for this question and compare it to the current word count. Identify the weakest sections of the response and the parts most suitable for tightening or summarising, then rewrite the response to land as close to the target as possible. Tighten phrasing first; only summarise or remove content where it adds the least value to the scoring criteria, and never strip out evidence that materially supports the answer. Apply the changes to the response and confirm the final word count."
  },
  {
    id: "strengthen-response",
    category: "review",
    title: "Strengthen this response",
    description: "Remove weak claims, sharpen evidence",
    prompt:
      "Strengthen this response against the question and the scoring/evaluation criteria. Identify the parts most likely to lose marks: weak evidence, vague claims, missed criteria, or generic language. Rewrite them so the answer scores more highly. Where a section already meets the criteria, leave it unchanged; do not invent content or strip out relevant content to the question. Use the content library where additional evidence is needed."
  },
  {
    id: "check-requirements-covered",
    category: "review",
    title: "Check all requirements covered",
    description: "Compliance check with detailed gap list",
    prompt:
      "Conduct a comprehensive compliance check of this response against all tender requirements. Identify any missing mandatory elements, flag potential compliance issues, and suggest specific additions or modifications to ensure full alignment with the stated requirements. Provide a detailed checklist of what has been addressed and what still needs attention."
  },
  {
    id: "add-more-evidence",
    category: "review",
    title: "Add more evidence",
    description: "Case studies, stats and proof points from library",
    prompt:
      "Enhance this response by adding more compelling evidence, supporting data, and concrete examples. Search through our content library for relevant case studies, statistics, and proof points that strengthen the claims made in this response. Ensure all evidence is directly relevant and adds credibility to our proposal."
  },
  {
    id: "assess-tone-consistency",
    category: "review",
    title: "Assess tone consistency",
    description: "Check the tone holds up across the wider bid",
    prompt:
      "Assess whether the tone of voice is consistent across the wider bid. First check whether a tone of voice has been selected for this bid — if one has, evaluate how closely each response matches it; if one hasn't been selected then no worries, just assess how consistent the tone is between the responses themselves. Identify any questions or sections whose tone is out of step with the rest and could do with tightening up, and explain what to adjust to bring them into line."
  },
  {
    id: "compare-buyer-priorities",
    category: "review",
    title: "Compare against buyer priorities",
    description: "Alignment gaps and what to add or reframe",
    prompt:
      "Compare this response against the buyer's priorities - find their stated requirements, evaluation criteria, and any known pain points related to this question. Highlight where the answer aligns well and where it under-emphasises or misses what the buyer cares most about, and suggest what to add or reframe."
  }
];

/**
 * Two text parts are joined by a blank line when a recipe is sent together with
 * extra user text (`"<prompt>\n\n<extra>"`). Shared so the sender and the
 * history renderer stay in agreement.
 */
export const RECIPE_TEXT_SEPARATOR = "\n\n";

/**
 * If `text` is a sent recipe — its full prompt, optionally followed by extra
 * user text — return the recipe's title and any trailing text. Returns null for
 * ordinary messages. Lets the chat history show "<title>" or "<title> + extra"
 * in place of the verbatim prompt the agent actually received.
 *
 * Matching is done against `recipes` in the current UI language (pass the
 * result of {@link usePromptRecipes}), since the sent prompt is localised — a
 * Spanish user sends, and matches against, the Spanish prompt.
 * @param text - The raw user-message text to inspect.
 * @param recipes - The resolved recipes to match against (current language).
 * @returns The matched title and trailing text, or null if not a recipe.
 */
export const matchRecipePrefix = (
  text: string,
  recipes: PromptRecipe[]
): { title: string; rest: string } | null => {
  for (const recipe of recipes) {
    if (text === recipe.prompt) return { title: recipe.title, rest: "" };
    const withGap = `${recipe.prompt}${RECIPE_TEXT_SEPARATOR}`;
    if (text.startsWith(withGap)) {
      return { title: recipe.title, rest: text.slice(withGap.length) };
    }
  }
  return null;
};

/**
 * The prompt recipes resolved into the current UI language. Each recipe's
 * title, description and prompt are looked up via i18n, falling back to the
 * English source in {@link PROMPT_RECIPES} when a translation is missing.
 * @returns The recipes (catalogue order) with localised text.
 */
export const usePromptRecipes = (): PromptRecipe[] => PROMPT_RECIPES;

/**
 * The category tabs/headings in display order. The pane has no i18n layer,
 * so these are the English labels (the web app localises them).
 * @returns The categories.
 */
export const useRecipeCategories = (): { key: RecipeCategory; label: string }[] => RECIPE_CATEGORIES;
